import { useState, useEffect, useRef } from 'react';
import { initGoogleMaps, createAutocomplete, searchPlaces } from '../services/googleMaps';

/**
 * Hook for Google Places autocomplete functionality
 */
export const useGooglePlaces = (options = {}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const autocompleteRef = useRef(null);
  const inputRef = useRef(null);

  const loadMaps = async () => {
    setIsLoading(true);
    setError(null);
    // #region agent log
    try {
      fetch('http://127.0.0.1:7242/ingest/0061837b-d31b-4bc9-b253-97f40228a2c8',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'useGooglePlaces.js:14',message:'loadMaps started',data:{},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'F'})}).catch(()=>{});
    } catch(e) {}
    // #endregion
    try {
      await initGoogleMaps();
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/0061837b-d31b-4bc9-b253-97f40228a2c8',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'useGooglePlaces.js:18',message:'initGoogleMaps succeeded',data:{},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'F'})}).catch(()=>{});
      // #endregion
      setIsLoaded(true);
      setIsLoading(false);
    } catch (err) {
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/0061837b-d31b-4bc9-b253-97f40228a2c8',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'useGooglePlaces.js:22',message:'initGoogleMaps failed',data:{error:err.message,stack:err.stack},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'F'})}).catch(()=>{});
      // #endregion
      // Provide user-friendly error message
      const errorMessage = err.message || 'Unable to load Google Maps. Please check your internet connection.';
      setError(errorMessage);
      setIsLoading(false);
      console.error('Google Maps loading error:', err);
    }
  };

  useEffect(() => {
    loadMaps();
  }, []);

  const initializeAutocomplete = (inputElement, autocompleteOptions = {}) => {
    if (!isLoaded) {
      const errorMsg = 'Google Maps not loaded yet';
      console.warn('Cannot initialize autocomplete:', errorMsg);
      setError(errorMsg);
      return null;
    }

    if (!inputElement) {
      const errorMsg = 'Input element not provided';
      console.warn('Cannot initialize autocomplete:', errorMsg);
      setError(errorMsg);
      return null;
    }

    // Wait a bit to ensure DOM is ready (especially on mobile)
    if (typeof inputElement.focus !== 'function') {
      const errorMsg = 'Invalid input element';
      console.warn('Input element is not a valid input:', inputElement);
      setError(errorMsg);
      return null;
    }

    // Check if Google Maps is actually available
    if (!window.google || !window.google.maps || !window.google.maps.places) {
      const errorMsg = 'Google Maps Places API not available. Please refresh the page.';
      console.error('Google Maps Places not available:', {
        hasGoogle: !!window.google,
        hasMaps: !!window.google?.maps,
        hasPlaces: !!window.google?.maps?.places,
      });
      setError(errorMsg);
      return null;
    }

    try {
      const autocomplete = createAutocomplete(inputElement, {
        ...options,
        ...autocompleteOptions,
      });

      if (!autocomplete) {
        const errorMsg = 'Failed to create autocomplete instance';
        console.error(errorMsg);
        setError(errorMsg);
        return null;
      }

      autocompleteRef.current = autocomplete;
      inputRef.current = inputElement;

      console.log('Autocomplete initialized successfully for:', inputElement.id || inputElement.name || 'unknown');
      setError(null); // Clear any previous errors
      return autocomplete;
    } catch (err) {
      const errorMsg = err.message || 'Unknown error initializing autocomplete';
      console.error('Error initializing autocomplete:', err);
      setError(errorMsg);
      return null;
    }
  };

  const search = async (query, cityBounds = null) => {
    if (!isLoaded) {
      throw new Error('Google Maps not loaded yet');
    }

    try {
      return await searchPlaces(query, cityBounds);
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  return {
    isLoaded,
    error,
    isLoading,
    retry: loadMaps,
    initializeAutocomplete,
    search,
    autocomplete: autocompleteRef.current,
  };
};

